import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { AuthService } from 'src/app/common/services/auth/auth.service';
import { APICoreService } from 'src/app/common/services/api-core/api-core.service';
import { StorageService } from 'src/app/common/services/storage/storage.service';
import { User } from 'src/app/common/state/user/user.model';
import { UserState } from 'src/app/common/state/user/user.state';
import {
  OAUTH_AUTHORIZE_REQUEST_KEY,
  OauthAuthorizeRequest,
} from 'src/app/common/utilities/oauth-authorize.helpers';

const CLIENT_DISPLAY_NAMES: { [clientId: string]: string } = {
  'growelab-mcp': 'The GroweLab AI assistant connection (MCP)',
};

@Component({
  selector: 'app-oauth-authorize',
  templateUrl: './oauth-authorize.component.html',
  styleUrls: ['./oauth-authorize.component.scss'],
  standalone: false,
})
export class OauthAuthorizeComponent implements OnInit {
  request: OauthAuthorizeRequest | null;

  user: User | null;

  loading = false;

  errorMessage: string | null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private store: Store,
    private authService: AuthService,
    private apiService: APICoreService
  ) {}

  ngOnInit() {
    const params = this.route.snapshot.queryParams;
    if (params['client_id']) {
      this.request = {
        client_id: params['client_id'],
        redirect_uri: params['redirect_uri'],
        code_challenge: params['code_challenge'],
        code_challenge_method: params['code_challenge_method'] || 'S256',
        state: params['state'],
        scope: params['scope'],
      };
      StorageService.storeItem(OAUTH_AUTHORIZE_REQUEST_KEY, this.request);
    } else {
      // Returning from a login that lost the query params (full-page redirect)
      this.request = StorageService.getItem(OAUTH_AUTHORIZE_REQUEST_KEY);
    }

    if (
      !this.request?.client_id ||
      !this.request?.redirect_uri ||
      !this.request?.code_challenge
    ) {
      this.errorMessage =
        'This authorization link is invalid or has expired. Please start the connection again from your application.';
      return;
    }

    if (!StorageService.getAuthToken()) {
      this.authService.redirectUrl = '/oauth/authorize';
      this.router.navigate(['/login']);
      return;
    }

    this.user = this.store.selectSnapshot(UserState.getUser);
  }

  get clientDisplayName(): string {
    if (!this.request) return '';
    return (
      CLIENT_DISPLAY_NAMES[this.request.client_id] || this.request.client_id
    );
  }

  authorize() {
    if (!this.request) return;

    const payload: { [key: string]: string } = {
      client_id: this.request.client_id,
      redirect_uri: this.request.redirect_uri,
      code_challenge: this.request.code_challenge,
      code_challenge_method: this.request.code_challenge_method,
    };
    if (this.request.scope) {
      payload['scope'] = this.request.scope;
    }

    this.loading = true;
    this.apiService.postRequest('auth/oauth-authorize', payload).subscribe({
      next: (res) => {
        StorageService.removeItem(OAUTH_AUTHORIZE_REQUEST_KEY);
        this.redirectBack({ code: res.code });
      },
      error: (err) => {
        this.loading = false;
        this.errorMessage =
          err.error?.message ||
          'Unable to complete the authorization. Please try again.';
      },
    });
  }

  deny() {
    StorageService.removeItem(OAUTH_AUTHORIZE_REQUEST_KEY);
    this.redirectBack({ error: 'access_denied' });
  }

  private redirectBack(params: { [key: string]: string }) {
    if (!this.request) return;

    if (this.request.state) {
      params['state'] = this.request.state;
    }
    const query = Object.entries(params)
      .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
      .join('&');
    const separator = this.request.redirect_uri.includes('?') ? '&' : '?';
    window.location.href = `${this.request.redirect_uri}${separator}${query}`;
  }
}
